import React, { useCallback, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import type { MediaInsertTabProps } from '@atlaskit/editor-plugin-media-insert';
import { AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { token } from '@atlaskit/tokens';
import { ImageFromAIGeneration } from '@atlassian/ai-image-generation/image-from-ai-generation';
import type { GenerateImageErrorParams } from '@atlassian/ai-image-generation/types';

const i18n = defineMessages({
	aiGenerationErrorFlagTitle: {
		id: 'editor-plugin-ai-image-generation.MediaInsertImageGenerationTab.aiGenerationErrorFlagTitle',
		description: 'Title within the error flag when AI image generation fails.',
		defaultMessage: "We couldn't generate your image",
	},
	aiGenerationErrorFlagDescription: {
		id: 'editor-plugin-ai-image-generation.MediaInsertImageGenerationTab.aiGenerationErrorFlagDescription',
		description: 'Description within the error flag when AI image generation fails.',
		defaultMessage: 'Try again or choose a different image.',
	},
	aiGenerationErrorFlagDescriptionWithReason: {
		id: 'editor-plugin-ai-image-generation.MediaInsertImageGenerationTab.aiGenerationErrorFlagDescriptionWithReason',
		description:
			'Description within the error flag when AI image generation fails with a specific error reason from the API.',
		defaultMessage: '{errorReason}. Try again or choose a different image.',
	},
});

/**
 * Adapter that renders `<ImageFromAIGeneration>` (a private
 * `@atlassian/ai-image-generation` component) inside the public media insert
 * picker as a registered tab.
 *
 * Lives in this private package (which already depends on the private
 * AI image-generation package) so that `@atlaskit/editor-plugin-media-insert`
 * never has to import any `@atlassian/*` package.
 *
 * Wired in via `api.mediaInsert.actions.registerInsertTab(...)` from
 * `aiImageGenerationPlugin`'s setup.
 */
export function MediaInsertImageGenerationTab({
	closeMediaInsertPicker,
	insertMediaSingle,
	mediaProvider,
}: MediaInsertTabProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const [errorFlag, setErrorFlag] = useState<{
		description: string;
		id: string;
	} | null>(null);

	const showAIGenerationErrorFlag = useCallback(
		({ error }: GenerateImageErrorParams) => {
			const description = error
				? formatMessage(i18n.aiGenerationErrorFlagDescriptionWithReason, {
						errorReason: error.message,
					})
				: formatMessage(i18n.aiGenerationErrorFlagDescription);

			setErrorFlag({
				id: crypto.randomUUID(),
				description,
			});
		},
		[formatMessage],
	);

	const dismissErrorFlag = useCallback(() => {
		setErrorFlag(null);
	}, []);

	const insertMedia = useCallback(
		({
			id,
			collection,
			fileMimeType,
			dimensions,
		}: {
			collection: string | undefined;
			dimensions?: { height: number; width: number };
			fileMimeType?: string;
			id: string | undefined;
		}) => {
			if (!id) {
				return;
			}
			insertMediaSingle({
				inputMethod: INPUT_METHOD.MEDIA_PICKER,
				mediaState: {
					id,
					collection,
					fileMimeType,
					dimensions,
				},
			});
		},
		[insertMediaSingle],
	);

	return (
		<>
			<ImageFromAIGeneration
				mediaUploadCollection={mediaProvider.uploadParams?.collection}
				insertMedia={insertMedia}
				closePopup={closeMediaInsertPicker}
				onError={showAIGenerationErrorFlag}
			/>
			<FlagGroup onDismissed={dismissErrorFlag}>
				{errorFlag && (
					<AutoDismissFlag
						id={errorFlag.id}
						title={formatMessage(i18n.aiGenerationErrorFlagTitle)}
						description={errorFlag.description}
						icon={<StatusErrorIcon label="" color={token('color.icon.danger')} />}
					/>
				)}
			</FlagGroup>
		</>
	);
}
